/**
 * The frame every Overview widget draws in: one register-style panel with an
 * eyebrow and an <h2>, an optional link on the right, and the body below.
 *
 * Each widget owns its own query and its own three states (loading, failed,
 * loaded). This frame owns none of them — it only keeps the panels looking like
 * one page.
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const WIDGET_EYEBROW = 'text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted';

interface WidgetPanelProps {
  /** Becomes `data-testid="widget-<key>"` and the heading's id. */
  widgetKey: string;
  eyebrow: string;
  title: string;
  /** A "View all"-style link to the page that holds the rows. */
  link?: { label: string; to: string };
  /** Anything else for the header's right-hand side (a toggle, a count). */
  aside?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * The panel grows to its Overview slot's height so a pair of half widgets
 * ends level (see OverviewScreen). Its children do not: the body keeps its
 * natural height and any room is left below it, never handed to a chart.
 */
export function WidgetPanel({ widgetKey, eyebrow, title, link, aside, children, className }: WidgetPanelProps) {
  const navigate = useNavigate();
  const headingId = `widget-${widgetKey}-title`;
  return (
    <section
      aria-labelledby={headingId}
      data-testid={`widget-${widgetKey}`}
      className={`min-w-0 flex-1 overflow-hidden rounded-fi-panel border border-fieldiq-border bg-fieldiq-card ${className ?? ''}`}
    >
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-fieldiq-border px-4 py-3">
        <div className="min-w-0">
          <p className={WIDGET_EYEBROW}>{eyebrow}</p>
          <h2 id={headingId} className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
            {title}
          </h2>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {aside}
          {link && (
            <button
              type="button"
              onClick={() => navigate(link.to)}
              className="inline-flex items-center gap-1 text-xs font-bold text-fieldiq-text underline decoration-fi-amber decoration-2 underline-offset-4"
            >
              {link.label} <ChevronRight className="size-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
      </header>
      {children}
    </section>
  );
}

/** Three pulsing rows: a widget whose read is in flight shows no figure at all. */
export function WidgetPending({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4" aria-busy="true" data-testid="widget-pending">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-10 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
      ))}
    </div>
  );
}

/**
 * A labelled figure inside a widget: label above number, as the band's stats
 * read, with NO surface of its own.
 *
 * It used to be a tile — its own edge and a faint page-grey wash — sitting in a
 * panel of the same surface, so four boxes inside a box blended into one grey
 * block. The tile is gone rather than given a stronger edge or tint: the label,
 * the weight of the figure and the grid's gap separate the figures, which is
 * how the band's strip does it.
 */
export function WidgetFigure({
  label,
  value,
  sub,
  testId,
  alert,
}: {
  label: string;
  /** Already formatted. An absent figure arrives as '—'. */
  value: React.ReactNode;
  sub?: React.ReactNode;
  testId?: string;
  /**
   * Marks the figure as the one that needs acting on: the label becomes a
   * saturated red badge with dark ink. A badge, not red text — a
   * `--fi-signal-*` hue is a fill, and as ink it reads ~2:1 on the light panel
   * (CLAUDE.md, "A --fi-signal-* hue is a fill to put dark ink on").
   */
  alert?: boolean;
}) {
  return (
    <div className="min-w-0" data-testid={testId}>
      {alert ? (
        <p>
          <span className="rounded-fi-pill border border-black/20 bg-fi-signal-red px-2 py-0.5 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fi-signal-ink">
            {label}
          </span>
        </p>
      ) : (
        <p className={WIDGET_EYEBROW}>{label}</p>
      )}
      <p className="mt-1 truncate text-lg font-extrabold tabular-nums text-fieldiq-text">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-fieldiq-muted">{sub}</p>}
    </div>
  );
}
